import AnimatorConflicts from "../../components/Meetings/AnimatorConflicts";
import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";
import { UpdateAnimatorsParams } from "./Meeting.d";

export const fetchMeeting = (meetingId: string, setMeeting: Function, setLoaded: Function) => {
  setLoaded(false);
  ApiService.get(
    `meetings/${meetingId}?get_attendees_info=true&get_attendees_flags=true&get_animators_info=true&get_meeting_type_info=true&get_meeting_cohort_info=true&get_program_info=true&get_project_info=true`
  )
    .then((response) => {
      setMeeting(response.data.meeting);
    })
    .catch((err) => {
      console.error("Error fetching meeting data:", err);
    })
    .finally(() => setLoaded(true));
};

export const updateMeeting = (
  meetingId: string,
  meetingData: any,
  language: string,
  setShowEditModal: Function,
  setIsLoading: Function,
  successCallback?: Function
) => {
  setIsLoading(true);
  meetingData.dt_start = new Date(meetingData.dt_start).toISOString();
  meetingData.dt_end = new Date(meetingData.dt_end).toISOString();
  ApiService.put(`meetings/${meetingId}`, meetingData)
    .then(() => {
      alert(translate(language, "PAGES.MEETING.SUCCESS.EDITED_MEETING"));
      setShowEditModal(false);
      successCallback?.();
    })
    .catch((err) => {
      console.error("Error updating meeting:", err);
      alert(translate(language, "PAGES.MEETING.ERRORS.EDITED_MEETING"));
    })
    .finally(() => {
      setIsLoading(false);
    });
};

export const deleteMeeting = (meetingId: string, language: string, setIsLoading: Function, callback?: Function) => {
  setIsLoading(true);
  ApiService.delete(`meetings/${meetingId}`)
    .then((_) => {
      callback?.();
    })
    .catch((err) => {
      console.error("Error deleting meeting:", err);
      alert(translate(language, "PAGES.MEETING.ERRORS.DELETE_MEETING"));
    })
    .finally(() => {
      setIsLoading(false);
    });
};

export const updateAnimators = ({
  meetingId,
  updatedAnimators,
  setSelectedAnimators,
  meetingLinkService,
  language,
  setIsLoading,
  displayAlert,
  successCallback,
  closeAlert,
  ignoreAnimatorsConflict = false,
  hostAnimatorId,
}: UpdateAnimatorsParams) => {
  setIsLoading(true);

  ApiService.put(`meetings/${meetingId}/animators`, {
    animator_ids: updatedAnimators.map((animator) => animator.staff_member_id),
    service_link_generation: meetingLinkService,
    ignore_animators_conflict: ignoreAnimatorsConflict,
    host_animator_id: hostAnimatorId ?? null,
  })
    .then(() => {
      setSelectedAnimators([...updatedAnimators]);

      displayAlert?.({
        type: "success",
        title: translate(language, "PAGES.MEETING.SUCCESS.UPDATED_ANIMATOR"),
        clickOutsideToClose: true,
      });

      successCallback?.();
    })
    .catch((error) => {
      console.error("Error updating animators:", error);
      const conflicts = error?.data?.error?.message?.animator_conflicts;
      if (conflicts) {
        displayAlert?.({
          type: "danger",
          title: translate(language, "PAGES.MEETING.ERRORS.ANIMATOR_CONFLICT.TITLE"),
          message: translate(language, "PAGES.MEETING.ERRORS.ANIMATOR_CONFLICT.MESSAGE"),
          extraContent: <AnimatorConflicts conflicts={conflicts} />,
          clickOutsideToClose: true,
          buttons: [
            {
              label: translate(language, "PAGES.MEETINGS.DST_ALERT.WARNING.OPEN_CONFLICTS"),
              variant: "secondary",
              onClick: () =>
                conflicts
                  .flatMap((conflict: any) => conflict.conflicting_meetings.map((meeting: any) => meeting.id))
                  .forEach((id: string) => window.open(`/meeting/${id}`, "_blank")),
            },
            {
              label: translate(language, "PAGES.MEETINGS.DST_ALERT.WARNING.FORCE_ADD"),
              variant: "warning",
              onClick: () => {
                closeAlert?.();
                updateAnimators({
                  meetingId,
                  updatedAnimators,
                  setSelectedAnimators,
                  meetingLinkService,
                  language,
                  setIsLoading,
                  displayAlert,
                  successCallback,
                  closeAlert,
                  ignoreAnimatorsConflict: true,
                });
              },
            },
            {
              label: translate(language, "GLOBAL.CLOSE"),
              variant: "primary",
              onClick: () => closeAlert?.(),
            },
          ],
        });
      } else {
        displayAlert?.({
          type: "danger",
          title: translate(language, "PAGES.MEETING.ERRORS.UPDATED_ANIMATOR"),
          clickOutsideToClose: true,
        });
      }
    })
    .finally(() => setIsLoading(false));
};

export const getBadgeData = (meeting: any, language: string) => {
  const badges = [
    {
      key: "VIP",
      iconClass: "dst-icon-star-01",
      description: translate(language, "PAGES.MEETING.BADGES.VIP"),
      count: 0,
    },
    {
      key: "B2B",
      iconClass: "dst-icon-image-user-check",
      description: translate(language, "PAGES.MEETING.BADGES.B2B"),
      count: 0,
    },
    {
      key: "Complicated",
      iconClass: "dst-icon-cloud-lightning",
      description: translate(language, "PAGES.MEETING.BADGES.COMPLICATED"),
      count: 0,
    },
    {
      key: "Extra Time",
      iconClass: "dst-icon-clock-stopwatch",
      description: translate(language, "PAGES.MEETING.BADGES.EXTRA_TIME"),
      count: 0,
    },
  ];

  meeting.attendees.forEach((attendee: any) => {
    attendee.flags?.forEach((flag: { name: string }) => {
      const badge = badges.find((b) => b.key === flag.name);
      if (badge) {
        badge.count++;
      }
    });

    if (attendee.extra_time) {
      const extraTimeBadge = badges.find((b) => b.key === "Extra Time");
      if (extraTimeBadge) {
        extraTimeBadge.count++;
      }
    }
  });

  return badges.filter((badge) => badge.count > 0).sort((a, b) => b.count - a.count);
};

export const fetchMeetingNotes = (meetingId: string, language: string, setNotes: Function, setIsLoading: Function) => {
  setIsLoading(true);

  ApiService.get(`meetings/${meetingId}/notes`)
    .then((response) => {
      setNotes(response.data.notes);
    })
    .catch((error) => {
      console.error("Error fetching meeting notes:", error);
      alert(translate(language, "PAGES.MEETING.ERRORS.FETCH_NOTES"));
    })
    .finally(() => {
      setIsLoading(false);
    });
};

export const addNoteToMeeting = (
  meetingId: string,
  content: string,
  language: string,
  setNote: Function,
  setNotes: Function,
  setIsLoading: Function
) => {
  if (!content.trim()) {
    alert(translate(language, "PAGES.MEETING.ERRORS.EMPTY_NOTE"));
    return;
  }

  ApiService.post(`meetings/${meetingId}/notes`, { content })
    .then(() => {
      alert(translate(language, "PAGES.MEETING.SUCCESS.ADD_NOTE"));
      setNote("");

      fetchMeetingNotes(meetingId, language, setNotes, setIsLoading);
    })
    .catch((error) => {
      console.error("Error adding note:", error);
      alert(translate(language, "PAGES.MEETING.ERRORS.ADD_NOTE"));
    });
};

export const updateNote = (
  meetingId: string,
  noteId: string,
  editedContent: string,
  language: string,
  setEditingNoteId: Function,
  setNotes: Function,
  setLoadingNotes: Function
) => {
  if (!editedContent.trim()) {
    alert(translate(language, "PAGES.MEETING.ERRORS.EMPTY_NOTE"));
    return;
  }

  ApiService.put(`meetings/${meetingId}/notes/${noteId}`, { content: editedContent })
    .then(() => {
      alert(translate(language, "PAGES.MEETING.SUCCESS.EDITED_NOTE"));
      setEditingNoteId(null);
      fetchMeetingNotes(meetingId, language, setNotes, setLoadingNotes);
    })
    .catch((err) => {
      console.error("Error updating note:", err);
      alert(translate(language, "PAGES.MEETING.ERRORS.EDITED_NOTE"));
    });
};

export const deleteNote = (
  meetingId: string,
  noteId: string,
  language: string,
  setNotes: Function,
  setLoadingNotes: Function
) => {
  ApiService.delete(`meetings/${meetingId}/notes/${noteId}`)
    .then(() => {
      alert(translate(language, "PAGES.MEETING.SUCCESS.DELETED_NOTE"));
      fetchMeetingNotes(meetingId, language, setNotes, setLoadingNotes);
    })
    .catch((error) => {
      console.error("Error deleting note:", error);
      alert(translate(language, "PAGES.MEETING.ERRORS.DELETED_NOTE"));
    });
};

export const updateMeetingRecords = (
  meetingId: string,
  password: string,
  link: string,
  language: string,
  setIsMeetingRecordsUpdating: Function,
  successCallback?: Function
) => {
  setIsMeetingRecordsUpdating(true);
  ApiService.put(`meetings/${meetingId}/records`, {
    password,
    link,
  })
    .then(() => {
      alert(translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_ZOOM_RECORD.ALERTS.SUCCESS"));
      successCallback?.();
    })
    .catch((error) => {
      console.error("Error updating meeting records:", error);
      alert(translate(language, "PAGES.MEETING.MANAGE_MEETING.EDIT_ZOOM_RECORD.ALERTS.ERROR"));
    })
    .finally(() => {
      setIsMeetingRecordsUpdating(false);
    });
};

export const fetchMeetingTypes = (setMeetingTypes: Function, setLoading: Function, language: string) => {
  setLoading(true);
  ApiService.get("meetings/types?get_suggested_names=true")
    .then((response) => {
      setMeetingTypes(response.data.meeting_types);
    })
    .catch((err) => {
      console.error("Error fetching meeting types:", err);
      alert(translate(language, "PAGES.MEETING.ERRORS.FETCH_MEETING_TYPES"));
    })
    .finally(() => {
      setLoading(false);
    });
};
